import { useLayoutEffect, useRef, useState } from "react";
import { DominoTile } from "./DominoTile";

// Snake layout (dominó de bar): fileiras horizontais que dobram por uma
// pedra de JUNÇÃO vertical perfeitamente alinhada à coluna da virada.
// - bucha (lados iguais) => sempre VERTICAL
// - demais pedras => HORIZONTAL (exceto quando são a pedra de junção)
// - pedras ligam por valores iguais (ordem do tabuleiro já garante o encadeamento)
function buildLayout(board, usableW) {
  const S = 42;          // 1 unidade (face menor / metade da pedra)
  const LONG = 84;       // 2S — face maior
  const GAP = 4;         // pequeno espaçamento entre as peças
  const usable = Math.max(usableW, LONG * 2);
  const n = board.length;
  const cells = [];
  const isDbl = (t) => t.left === t.right;

  let dir = 1;           // +1 Leste (→), -1 Oeste (←)
  let cursor = 0;        // fronteira: borda direita (dir=+1) ou esquerda (dir=-1)
  let baseline = 0;      // linha de centro (Y) da fileira atual
  let rowHasCell = false;
  let lastCell = null;   // última pedra colocada na fileira (p/ junção)

  for (let i = 0; i < n; i++) {
    const t = board[i];
    const isDouble = isDbl(t);
    // bucha (lados iguais): SEMPRE vertical, inclusive na saída; pedra normal: horizontal
    const vertical = isDouble;
    const w = vertical ? S : LONG;
    const h = vertical ? LONG : S;

    const overflow =
      (dir === 1 && cursor + w > usable) ||
      (dir === -1 && cursor - w < 0);

    if (rowHasCell && overflow && i !== n - 1) {
      // TURN: a pedra i vira JUNÇÃO (vertical) e a serpente desce p/ a próxima fileira.
      const endDouble = lastCell && lastCell.vertical && isDbl(lastCell.t);
      const VGAP = 6;
      let jleft, jtop;
      if (endDouble) {
        // REGRA 2+3: bucha terminou (vertical) → junção empilha ABAIXO, centro-X alinhado (face menor).
        jleft = lastCell.left + lastCell.w / 2 - S / 2;
        jtop = lastCell.top + lastCell.h;
      } else {
        // Fim com pedra horizontal → junção encosta na ponta (face menor) da última pedra e desce.
        jleft = dir === 1 ? lastCell.left + lastCell.w + GAP : lastCell.left - S - GAP;
        jtop = baseline - S / 2;
      }
      // Nova fileira em "L": começa ABAIXO da junção (sem sobrepor nada); passo entre fileiras >= LONG + VGAP
      const newBaseline = jtop + LONG + VGAP + S / 2;
      const jcell = { t, left: jleft, top: jtop, w: S, h: LONG, vertical: true, junction: true, flip: false, last: i === n - 1 };
      cells.push(jcell);
      lastCell = jcell;
      baseline = newBaseline;
      if (dir === 1) { dir = -1; cursor = jleft + S; }
      else { dir = 1; cursor = jleft; }
      rowHasCell = false;
      continue;
    }

    const top = baseline - h / 2; // centrado na linha de centro da fileira
    const flip = !vertical && dir === -1; // fileira voltando (←) → inverter lados p/ os valores baterem na ligação
    let left;
    if (dir === 1) { left = cursor; cursor += w + GAP; }
    else { left = cursor - w; cursor -= w + GAP; }
    const cell = { t, left, top, w, h, vertical, junction: false, flip, last: i === n - 1 };
    cells.push(cell);
    lastCell = cell;
    rowHasCell = true;
  }

  let minL = Infinity, minT = Infinity, maxR = -Infinity, maxB = -Infinity;
  cells.forEach((c) => {
    minL = Math.min(minL, c.left);
    minT = Math.min(minT, c.top);
    maxR = Math.max(maxR, c.left + c.w);
    maxB = Math.max(maxB, c.top + c.h);
  });
  if (!cells.length) { minL = minT = 0; maxR = maxB = 1; }
  cells.forEach((c) => { c.left -= minL; c.top -= minT; });
  return { cells, L: LONG, boundsW: maxR - minL, boundsH: maxB - minT };
}

// Escolhe a quantidade de fileiras que MAXIMIZA o tamanho das peças (melhor
// aproveitamento do feltro): testa de 1 a N fileiras e pega a maior escala.
function pickBest(board, W, H) {
  if (!board.length) return buildLayout(board, W);
  const pad = 16;
  let total = 0;
  board.forEach((t) => { total += t.left === t.right ? 42 : 84; });
  const maxRows = Math.min(8, board.length);
  let best = null;
  for (let r = 1; r <= maxRows; r++) {
    const lay = buildLayout(board, total / r + 84);
    const sc = Math.min((W - pad * 2) / Math.max(lay.boundsW, 1), (H - pad * 2) / Math.max(lay.boundsH, 1));
    if (!best || sc > best.sc) best = { ...lay, sc };
  }
  return best;
}

export function DominoBoard({ board = [] }) {
  const ref = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((e) => setBox({ w: e[0].contentRect.width, h: e[0].contentRect.height }));
    ro.observe(el);
    setBox({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  const { cells, L, boundsW, boundsH } = pickBest(board, box.w || 600, box.h || 400);
  const pad = 16;
  const W = box.w || 600, H = box.h || 400;
  const raw = Math.min(
    (W - pad * 2) / Math.max(boundsW, 1),
    (H - pad * 2) / Math.max(boundsH, 1),
  );
  const scale = board.length ? Math.max(0.32, Math.min(raw || 1, 1.7)) : 1;
  const offX = (W - boundsW * scale) / 2;
  const offY = (H - boundsH * scale) / 2;

  return (
    <div ref={ref} className="absolute inset-0 overflow-hidden" data-testid="domino-board">
      {board.length === 0 ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-white/50 font-head text-sm">Mesa vazia — aguardando primeira pedra</span>
        </div>
      ) : (
        <div style={{ position: "absolute", left: offX, top: offY, width: boundsW, height: boundsH, transform: `scale(${scale})`, transformOrigin: "top left" }}>
          {cells.map((c, i) => (
            <div key={`${i}-${c.t.id}`} style={{ position: "absolute", left: c.left, top: c.top }}>
              <DominoTile left={c.flip ? c.t.right : c.t.left} right={c.flip ? c.t.left : c.t.right} orientation={c.vertical ? "v" : "h"} size={L} corner={c.junction} last={c.last} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
